/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Provides access to the attribute-assignment resource of the assignment service.
 *
 * Features:
 * - `loadAttributeAssignments$()` — fetches assignments filtered by optional
 *   `userId` / `passId` query params. Updates the `attributeAssignments` signal
 *   on success when `passId` is provided (Pattern A).
 * - `sendReminder$()` — POSTs a reminder request and maps the `reminded` flag
 *   to a boolean result, rejecting when the server returns `reminded: false`
 *   (Pattern B).
 * - `deleteAttributeAssignment$()` — deletes an assignment and emits `true`
 *   on success (Pattern A).
 * - `createAssignments$()` — bulk-creates assignments (Pattern A).
 * - Deprecated Promise wrappers kept for backward compatibility.
 * - Pure helper methods `mapDTOToAssignmentAttributes`,
 *   `mapAssignmentAttributesToDTO`, and `filterAttributesByAssignments` are
 *   unchanged — no HTTP involvement.
 */

import {HttpParams} from '@angular/common/http';
import {inject, Injectable, signal} from '@angular/core';
import {firstValueFrom, map, Observable, tap} from 'rxjs';

import {environment} from '../environments/environment';
import {AssignmentDto} from '../models/battery/AssignmentRequestDto';
import {BatteryPassAttributeLabel} from '../models/BatteryPassAttributeLabel';
import {BatteryPassSelection} from '../models/BatteryPassAttributeSelection';
import {FormCategory, FormFieldAttribute} from '../models/FormCategory';
import {ApiClient} from './api-client.service';

/** Base URL for the attribute-assignment resource. */
const ASSIGNMENT_BASE = `${environment.backend.assignment_host}/attribute-assignments`;

/**
 * Builds the list URL with optional query params, mirroring the original logic:
 * both `userId` and `passId` are only appended when non-empty.
 */
function buildAssignmentListUrl(userId?: string, passId?: string): string {
  let params = new HttpParams();
  if (userId) {
    params = params.append('assignee', userId);
  }
  if (passId) {
    params = params.append('passportId', passId);
  }
  return `${ASSIGNMENT_BASE}?${params.toString()}`;
}

/** Shape of the paginated list response. */
interface AssignmentListResponse {
  readonly data: AssignmentDto[];
}

/** Shape of the reminder response. */
interface ReminderResponse {
  readonly reminded: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AssignmentService {
  private readonly apiClient = inject(ApiClient);

  readonly attributeSelections = signal<{
    [key: string]: BatteryPassSelection[];
  }>({});
  readonly attributeAssignments = signal<{
    [key: string]: BatteryPassAttributeLabel[];
  }>({});

  originalAttributeAssignments: {
    [key: string]: BatteryPassAttributeLabel[];
  } = {};

  // ─── Observable API (new) ────────────────────────────────────────────────────

  /**
   * Returns attribute assignments filtered by optional `userId` and `passId`.
   *
   * When `passId` is provided, updates the `attributeAssignments` signal and
   * `originalAttributeAssignments` map after a successful response.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param userId - Optional assignee filter.
   * @param passId - Optional passport ID filter. Also keys the signal cache.
   * @returns An `Observable` emitting the mapped `BatteryPassAttributeLabel[]`.
   */
  loadAttributeAssignments$(
    userId?: string,
    passId?: string,
  ): Observable<BatteryPassAttributeLabel[]> {
    return this.apiClient
      .get<AssignmentListResponse>(buildAssignmentListUrl(userId, passId))
      .pipe(
        map((response) => this.mapDTOToAssignmentAttributes(response.data)),
        tap((mappedData) => {
          if (passId) {
            this.originalAttributeAssignments[passId] = mappedData;
            this.attributeAssignments.set(this.originalAttributeAssignments);
          }
        }),
      );
  }

  /**
   * Sends a reminder for the given assignment ID.
   *
   * Emits `true` when the server confirms `reminded: true`, or errors with
   * `false` when the server returns `reminded: false` (Pattern B).
   *
   * @param id - The assignment ID to send a reminder for.
   * @returns An `Observable` emitting `true` on success.
   */
  sendReminder$(id: string): Observable<true> {
    return this.apiClient
      .post<ReminderResponse>(`${ASSIGNMENT_BASE}/${id}/remind`)
      .pipe(
        map((response) => {
          if (response.reminded === true) {
            return true as const;
          }
          throw new Error('Reminder was not sent');
        }),
      );
  }

  /**
   * Deletes the attribute assignment with the given ID.
   *
   * Emits `true` on success. Errors propagate to `GlobalErrorHandlerService`
   * automatically (Pattern A).
   *
   * @param id - The assignment ID to delete.
   * @returns An `Observable` emitting `true` on success.
   */
  deleteAttributeAssignment$(id: string): Observable<true> {
    return this.apiClient
      .delete<void>(`${ASSIGNMENT_BASE}/${id}`)
      .pipe(map(() => true as const));
  }

  /**
   * Bulk-creates attribute assignments for the given passport.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param attributeLabelData - The attributes to assign.
   * @param id - The passport ID.
   * @returns An `Observable` that completes when the request succeeds.
   */
  createAssignments$(
    attributeLabelData: BatteryPassAttributeLabel[],
    id: string,
  ): Observable<unknown> {
    const mappedAttributes = this.mapAssignmentAttributesToDTO(
      attributeLabelData,
      id,
    );
    return this.apiClient.post<unknown>(ASSIGNMENT_BASE, mappedAttributes);
  }

  // ─── Deprecated Promise wrappers (backward-compat) ──────────────────────────

  /** @deprecated Use `loadAttributeAssignments$()` instead. */
  async loadAttributeAssignments(
    userId?: string,
    passId?: string,
  ): Promise<BatteryPassAttributeLabel[]> {
    return firstValueFrom(this.loadAttributeAssignments$(userId, passId));
  }

  /** @deprecated Use `sendReminder$()` instead. */
  async sendReminder(id: string): Promise<true> {
    return firstValueFrom(this.sendReminder$(id));
  }

  /** @deprecated Use `deleteAttributeAssignment$()` instead. */
  async deleteAttributeAssignment(id: string): Promise<boolean> {
    return firstValueFrom(this.deleteAttributeAssignment$(id));
  }

  /** @deprecated Use `createAssignments$()` instead. */
  async createAssignments(
    attributeLabelData: BatteryPassAttributeLabel[],
    id: string,
  ): Promise<void> {
    await firstValueFrom(this.createAssignments$(attributeLabelData, id));
  }

  // ─── Pure helpers (unchanged) ────────────────────────────────────────────────

  mapDTOToAssignmentAttributes(
    data: AssignmentDto[],
  ): BatteryPassAttributeLabel[] {
    return data.map((assignment) => {
      return {
        id: assignment.id,
        status: assignment.status,
        passportId: assignment.passportId,
        assigneeId: assignment.assignee,
        assetId: assignment.assetId,
        assigneeEmail: '',
        attribute: assignment.attribute.replace('passport.', ''),
        masterChildFlag: assignment.masterChildFlag,
        createdAt: assignment.createdAt,
      };
    });
  }

  mapAssignmentAttributesToDTO(
    data: BatteryPassAttributeLabel[],
    passportId: string,
  ) {
    return {
      data: data.map((assignment) => {
        return {
          passportId,
          assignee: assignment.assigneeId,
          attribute: `passport.${assignment.attribute}`,
        };
      }),
    };
  }

  /** This function enables a subset of the passport structure for attribute
   *  assignments.
   *
   * @param formStructure
   * @param attributeAssignments
   */
  filterAttributesByAssignments(
    formStructure: FormCategory[],
    attributeAssignments: Array<{attribute: string}>,
  ): FormCategory[] {
    const filteredStructure = JSON.parse(JSON.stringify(formStructure));

    // Helper function to get full path
    const getFullPath = (currentPath: string, additionalPath?: string) => {
      if (!additionalPath) {
        return currentPath;
      }
      return currentPath ? `${currentPath}.${additionalPath}` : additionalPath;
    };

    // Helper function to check if path exists in assignments
    const isPathInAssignments = (path: string): boolean => {
      return attributeAssignments.some(
        (assignment) => assignment.attribute === path,
      );
    };

    // Recursive function to filter attributes
    const filterAttributes = (
      obj: FormFieldAttribute | FormFieldAttribute[],
      currentPath = '',
    ): any => {
      if (!obj) {
        return obj;
      }

      // Handle arrays
      if (Array.isArray(obj)) {
        return obj
          .map((item) => filterAttributes(item, currentPath))
          .filter((item) => item !== null);
      }

      if (typeof obj !== 'object') {
        return obj;
      }

      if (hasValuePath(obj)) {
        const fullPath = getFullPath(currentPath, obj.valuePath);
        return isPathInAssignments(fullPath) ? obj : null;
      }

      // Process nested attributes
      if (obj.attributes) {
        const newPath = obj.valuePath
          ? getFullPath(currentPath, obj.valuePath)
          : currentPath;

        obj.attributes = obj.attributes
          .map((attr) => filterAttributes(attr, newPath))
          .filter((attr) => attr !== null);

        // If no attributes left after filtering, return null
        if (obj.attributes.length === 0) {
          return null;
        }
      }

      // Process all other properties
      if (typeof obj.attributes === 'object') {
        obj.attributes = filterAttributes(obj.attributes, currentPath);
      }

      return obj;
    };

    function hasValuePath(obj: FormFieldAttribute): boolean {
      return !!obj && typeof obj === 'object' && !!obj.valuePath;
    }

    return filterAttributes(filteredStructure);
  }
}
